import React, { useEffect, useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import api from "views/auth/Api";
import { API_BASE_URL } from "configs/AppConfig";
import { message } from "antd";
import Card from "components/card";
import Loader from "components/loader";
import { FiSearch } from "react-icons/fi";
import { MdOutlineLockOpen, MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { TbArrowsSort, TbSortAscending, TbSortDescending } from "react-icons/tb";
import "../library/library.css";

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const classLabel = (student) =>
  student.class?.grade?.grade
    ? `${student.class.grade.grade} - ${student.class.class}`
    : student.class?.class || "";

const StudentsManagement = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchStudents = () => {
    setLoading(true);
    api
      .get(`${API_BASE_URL}student`)
      .then((response) => {
        setStudents(response.data || []);
      })
      .catch((error) => {
        console.log("error fetching students ", error);
        message.error(
          error.response?.data?.message || "Failed to load students ❌"
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openReset = (student) => {
    setResetTarget(student);
    setPassword("");
    setConfirmPassword("");
  };

  const closeReset = () => {
    setResetTarget(null);
    setPassword("");
    setConfirmPassword("");
  };

  const submitReset = (event) => {
    event.preventDefault();

    if (password.trim().length < 6) {
      message.error("Password must be at least 6 characters long");
      return;
    }

    if (password !== confirmPassword) {
      message.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    api
      .patch(`${API_BASE_URL}student/${resetTarget.id}/reset-password`, {
        password,
      })
      .then(() => {
        message.success(`Password reset for ${resetTarget.name}`);
        closeReset();
      })
      .catch((error) => {
        console.log("error resetting password ", error);
        message.error(
          error.response?.data?.message || "Failed to reset password ❌"
        );
      })
      .finally(() => setSubmitting(false));
  };

  const [classFilter, setClassFilter] = useState("all");

  const columns = useMemo(
    () => [
      {
        Header: "NAME",
        accessor: "name",
      },
      {
        Header: "USERNAME",
        accessor: "userName",
      },
      {
        Header: "EMAIL",
        accessor: "email",
      },
      {
        Header: "CLASS",
        accessor: (row) => classLabel(row),
        id: "class",
      },
      {
        Header: "ACTION",
        id: "action",
        disableSortBy: true,
      },
    ],
    []
  );

  const classOptions = useMemo(() => {
    const labels = new Set(
      students.map((student) => classLabel(student)).filter(Boolean)
    );
    return Array.from(labels).sort();
  }, [students]);

  const data = useMemo(() => {
    if (classFilter === "all") return students;
    return students.filter((student) => classLabel(student) === classFilter);
  }, [students, classFilter]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, globalFilter },
  } = useTable(
    { columns, data, initialState: { pageSize: 20 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <div className="h-fit w-full">
      <div className="libraryHeader">
        <div className="libraryH">
          <h1>Students Management</h1>
        </div>
        <div className="libraryheaderImg"></div>
      </div>

      <Card extra={"mt-5 mb-8 w-full p-4"}>
        <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
            {students.length} {students.length === 1 ? "student" : "students"}{" "}
            in your school
          </p>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className="h-10 w-full rounded-full bg-lightPrimary px-4 text-sm font-medium text-navy-700 outline-none dark:bg-navy-900 dark:text-white sm:w-[160px]"
            >
              <option value="all">All classes</option>
              {classOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <div className="flex h-10 w-full items-center gap-2 rounded-full bg-lightPrimary px-4 dark:bg-navy-900 sm:w-[260px]">
              <FiSearch className="h-4 w-4 shrink-0 text-gray-400 dark:text-white" />
              <input
                type="text"
                placeholder="Search students..."
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="h-full w-full bg-transparent text-sm font-medium text-navy-700 outline-none placeholder:text-gray-400 dark:text-white"
              />
            </div>
          </div>
        </div>

        <Loader loading={loading} />

        {!loading && data.length === 0 ? (
          <p className="p-8 text-center text-sm font-bold text-navy-700 dark:text-white">
            {students.length === 0
              ? "No students found."
              : "No students match this class."}
          </p>
        ) : null}

        {!loading && data.length > 0 ? (
          <>
            <div className="tableOverFlow overflow-x-auto">
              <table {...getTableProps()} className="w-full">
                <thead>
                  {headerGroups.map((headerGroup, hIndex) => (
                    <tr {...headerGroup.getHeaderGroupProps()} key={hIndex}>
                      {headerGroup.headers.map((column, cIndex) => (
                        <th
                          {...column.getHeaderProps(
                            column.disableSortBy
                              ? {}
                              : column.getSortByToggleProps()
                          )}
                          key={cIndex}
                          className="border-b border-gray-200 pb-[10px] pr-6 text-start dark:!border-navy-700"
                        >
                          <div className="flex items-center gap-1 text-xs tracking-wide text-gray-600 dark:text-gray-400">
                            {column.render("Header")}
                            {!column.disableSortBy && (
                              <span className="text-sm">
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <TbSortDescending />
                                  ) : (
                                    <TbSortAscending />
                                  )
                                ) : (
                                  <TbArrowsSort className="opacity-40" />
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row, rIndex) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        key={rIndex}
                        className="border-b border-gray-100 last:border-0 dark:border-navy-700"
                      >
                        {row.cells.map((cell, cIndex) => {
                          let content;
                          if (cell.column.id === "name") {
                            content = (
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">
                                  {initials(row.original.name)}
                                </div>
                                <p className="text-sm font-bold text-navy-700 dark:text-white">
                                  {row.original.name}
                                </p>
                              </div>
                            );
                          } else if (cell.column.id === "userName") {
                            content = (
                              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                {row.original.userName || (
                                  <span className="italic text-gray-400">
                                    no account yet
                                  </span>
                                )}
                              </p>
                            );
                          } else if (cell.column.id === "email") {
                            content = (
                              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                {row.original.email}
                              </p>
                            );
                          } else if (cell.column.id === "class") {
                            content = (
                              <span className="rounded-full bg-lightPrimary px-3 py-1 text-xs font-bold text-navy-700 dark:bg-navy-900 dark:text-white">
                                {cell.render("Cell")}
                              </span>
                            );
                          } else if (cell.column.id === "action") {
                            content = (
                              <button
                                type="button"
                                disabled={!row.original.userName}
                                title={
                                  row.original.userName
                                    ? "Reset student password"
                                    : "Student has no account yet"
                                }
                                className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-bold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-brand-500"
                                onClick={() => openReset(row.original)}
                              >
                                <MdOutlineLockOpen className="h-4 w-4" />
                                Reset Password
                              </button>
                            );
                          }
                          return (
                            <td
                              className="py-3 pr-6"
                              {...cell.getCellProps()}
                              key={cIndex}
                            >
                              {content}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Page {pageIndex + 1} of {pageOptions.length || 1}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                  className="rounded-lg px-2 py-1 text-xs font-bold text-navy-700 disabled:opacity-30 dark:text-white"
                >
                  First
                </button>
                <button
                  type="button"
                  onClick={previousPage}
                  disabled={!canPreviousPage}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-lightPrimary text-navy-700 disabled:opacity-30 dark:bg-navy-900 dark:text-white"
                >
                  <MdKeyboardArrowLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: pageCount }, (_, i) => i)
                  .filter(
                    (i) =>
                      i === 0 ||
                      i === pageCount - 1 ||
                      Math.abs(i - pageIndex) <= 1
                  )
                  .reduce((acc, i, idx, arr) => {
                    if (idx > 0 && i - arr[idx - 1] > 1) acc.push("gap");
                    acc.push(i);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === "gap" ? (
                      <span
                        key={`gap-${idx}`}
                        className="px-1 text-xs text-gray-400"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        onClick={() => gotoPage(item)}
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition ${
                          pageIndex === item
                            ? "bg-brand-500 text-white"
                            : "text-navy-700 hover:bg-lightPrimary dark:text-white dark:hover:bg-navy-900"
                        }`}
                      >
                        {item + 1}
                      </button>
                    )
                  )}
                <button
                  type="button"
                  onClick={nextPage}
                  disabled={!canNextPage}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-lightPrimary text-navy-700 disabled:opacity-30 dark:bg-navy-900 dark:text-white"
                >
                  <MdKeyboardArrowRight className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                  className="rounded-lg px-2 py-1 text-xs font-bold text-navy-700 disabled:opacity-30 dark:text-white"
                >
                  Last
                </button>
              </div>
            </div>
          </>
        ) : null}
      </Card>

      {resetTarget ? (
        <div className="modal">
          <div className="fixed left-1/2 top-1/2 w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/10 text-brand-500">
                <MdOutlineLockOpen className="h-6 w-6" />
              </div>
              <div>
                <h4 className="text-base font-bold text-navy-700 dark:text-white">
                  Reset password
                </h4>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {resetTarget.name}
                </p>
              </div>
            </div>
            <form onSubmit={submitReset}>
              <label className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">
                New password
              </label>
              <input
                type="password"
                className="mb-3 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
              <label className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">
                Confirm password
              </label>
              <input
                type="password"
                className="mb-4 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm font-bold text-navy-700 hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700"
                  onClick={closeReset}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-60"
                  disabled={submitting}
                >
                  {submitting ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StudentsManagement;
